// Details in a banner's text, as the page sees them. The server fills them in per person
// and refuses a send it could not fill. On this page a token is never shown raw: the phone
// preview fills it with an invented sample, and everywhere else it reads as its plain label.

import type { BannerToken } from './bannerApi';

/** Anything shaped like a token: what the server checks a send against. */
const ANY_TOKEN_RE = /\{\{[^{}\r\n]*\}\}/g;
/** Only these are tokens on a phone: an old banner holding "{{x}}" reads as it always did. */
const KNOWN_TOKEN_RE =
  /\{\{[ \t]*(?:person\.[a-z_]+|store\.[a-z_]+|employee\.[a-z_]+|store\.custom\.[0-9a-f-]{36}|employee\.custom\.[0-9a-f-]{36}|target|figure)[ \t]*\}\}/gi;

/** `{{ store.name }}` → `store.name`. */
export const tokenNameOf = (raw: string): string => raw.replace(/^\{\{[ \t]*|[ \t]*\}\}$/g, '').toLowerCase();

const FIGURE_TOKENS = ['target', 'figure'];

/** Invented, and only ever on the admin's own preview. */
const SAMPLES: Record<string, string> = {
  'person.first_name': 'Asha',
  'person.name': 'Asha Menon',
  'person.code': '100231',
  'store.name': 'Demo Store',
};

/** Their words, for a stored title read before the project's list is loaded. */
const PLAIN_LABELS: Record<string, string> = {
  'person.first_name': 'First name',
  'person.name': 'Full name',
  'person.code': 'Employee code',
  'store.name': 'Store',
  target: 'Target',
  figure: 'Your figure',
};

const titleCase = (text: string): string => {
  const words = text.replace(/_/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/** A token's plain name: the server's label when the list is loaded, its own words when not. */
export const tokenLabel = (name: string, tokens: BannerToken[]): string => {
  const listed = tokens.find((t) => tokenNameOf(t.token) === name);
  if (listed) return listed.label;
  if (name in PLAIN_LABELS) return PLAIN_LABELS[name];
  if (name.includes('.custom.')) return 'A detail';
  const [scope, rest] = name.split('.');
  if (!rest) return titleCase(scope);
  return scope === 'store' ? `Store ${rest.replace(/_/g, ' ')}` : titleCase(rest);
};

/** The text as the phone preview shows it. `figures`: this form's own target and figure. */
export const fillForPreview = (
  text: string,
  tokens: BannerToken[],
  figures: { target?: string; figure?: string } = {}
): string =>
  text.replace(ANY_TOKEN_RE, (raw) => {
    const name = tokenNameOf(raw);
    return SAMPLES[name] ?? (name === 'target' ? figures.target : name === 'figure' ? figures.figure : undefined) ?? tokenLabel(name, tokens);
  });

/** Stored text on Sent history and the tracker: "Hi [First name], ...". */
export const tokensAsLabels = (text: string, tokens: BannerToken[] = []): string =>
  text.replace(KNOWN_TOKEN_RE, (raw) => `[${tokenLabel(tokenNameOf(raw), tokens)}]`);

/**
 * What each token reads as in the title and message boxes, by its name. Two details can share
 * a label, a store's "Zone" and a person's, and the box must turn each back into its own token:
 * those take their group too, and one that would still be taken stays a token.
 */
const boxLabels = (tokens: BannerToken[]): Map<string, string> => {
  const uses = new Map<string, number>();
  for (const t of tokens) uses.set(t.label, (uses.get(t.label) ?? 0) + 1);
  const taken = new Set<string>();
  const out = new Map<string, string>();
  for (const t of tokens) {
    const label = (uses.get(t.label) ?? 0) > 1 ? `${t.group} ${t.label}` : t.label;
    if (taken.has(label) || /[[\]]/.test(label)) continue;
    taken.add(label);
    out.set(tokenNameOf(t.token), label);
  }
  return out;
};

/** The text as the boxes show it: "Hello [Store Format]". What is stored stays the token. */
export const tokensToBox = (text: string, tokens: BannerToken[]): string => {
  const labels = boxLabels(tokens);
  return text.replace(KNOWN_TOKEN_RE, (raw) => {
    const label = labels.get(tokenNameOf(raw));
    return label ? `[${label}]` : raw;
  });
};

/** What a box says, back as the text to store: each label a token again, the rest as typed. */
export const boxToTokens = (text: string, tokens: BannerToken[]): string => {
  const byLabel = new Map([...boxLabels(tokens)].map(([name, label]) => [label, name]));
  return text.replace(/\[([^[\]\r\n]+)\]/g, (raw, label: string) => {
    const name = byLabel.get(label);
    return name ? `{{${name}}}` : raw;
  });
};

/** Why the server would refuse this text, in the server's own rules. Null when it would not. */
export const tokenProblem = (
  texts: string[],
  tokens: BannerToken[],
  allowFigure: boolean,
  /** Why not, where a figure is refused on a task that otherwise has one. */
  figureRefusal = 'Target and Your figure work only on Reach a level.'
): string | null => {
  // A token broken across lines, or one inside another, could only reach phones as typed.
  if (texts.some((t) => /\{\{|\}\}/.test(t.replace(ANY_TOKEN_RE, '')))) {
    return 'A detail in the text is not written properly.';
  }
  const names = texts.flatMap((t) => (t.match(ANY_TOKEN_RE) ?? []).map(tokenNameOf));
  if (names.length === 0) return null;
  // Text of nothing but details could read as nothing at all for someone. The server's test:
  // what is left must hold a letter or a digit, so "{{store.name}} 🎉" has no words of its own.
  if (texts.some((t) => t !== t.replace(ANY_TOKEN_RE, '') && !/[\p{L}\p{N}]/u.test(t.replace(ANY_TOKEN_RE, '')))) {
    return 'The title and message need some words of their own.';
  }
  if (!allowFigure && names.some((n) => FIGURE_TOKENS.includes(n))) {
    return figureRefusal;
  }
  if (tokens.length > 0) {
    const offered = new Set(tokens.map((t) => tokenNameOf(t.token)));
    if (names.some((n) => !offered.has(n))) return 'A detail in the text is not on this project.';
  }
  return null;
};
